import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { QuizLearnerIdentityType } from 'app/types'
import {
    readIdentificationDismissed,
    readQuizIdentity,
    writeIdentificationDismissed,
    writeQuizIdentity,
} from 'components/QuizIdentification/quiz-identity-storage'

import { AuthContext } from './AuthProvider'

type QuizIdentityContextValue = {
    getIdentity: (_orgId: string) => QuizLearnerIdentityType | null
    isDismissed: (_playbookId: string) => boolean
    identify: (_orgId: string, _identity: QuizLearnerIdentityType) => void
    dismiss: (_playbookId: string) => void
}

export const QuizIdentityContext = createContext<QuizIdentityContextValue>({
    getIdentity: () => null,
    isDismissed: () => false,
    identify: () => undefined,
    dismiss: () => undefined,
})

type Props = {
    children: JSX.Element
}

export const QuizIdentityProvider = ({ children }: Props) => {
    const { user } = useContext(AuthContext)
    const uid = user?.uid

    const [identitiesByOrgId, setIdentitiesByOrgId] = useState<Record<string, QuizLearnerIdentityType>>({})
    const [dismissedPlaybookIds, setDismissedPlaybookIds] = useState<Record<string, true>>({})

    useEffect(() => {
        setIdentitiesByOrgId({})
    }, [uid])

    const getIdentity = useCallback(
        (orgId: string) => identitiesByOrgId[orgId] ?? (uid ? readQuizIdentity(uid, orgId) : null),
        [identitiesByOrgId, uid],
    )

    const isDismissed = useCallback(
        (playbookId: string) => Boolean(dismissedPlaybookIds[playbookId]) || readIdentificationDismissed(playbookId),
        [dismissedPlaybookIds],
    )

    const identify = useCallback(
        (orgId: string, nextIdentity: QuizLearnerIdentityType) => {
            if (uid) writeQuizIdentity(uid, orgId, nextIdentity)
            setIdentitiesByOrgId(previous => ({ ...previous, [orgId]: nextIdentity }))
        },
        [uid],
    )

    const dismiss = useCallback((playbookId: string) => {
        writeIdentificationDismissed(playbookId)
        setDismissedPlaybookIds(previous => ({ ...previous, [playbookId]: true }))
    }, [])

    const value = useMemo(
        () => ({ getIdentity, isDismissed, identify, dismiss }),
        [getIdentity, isDismissed, identify, dismiss],
    )

    return <QuizIdentityContext.Provider value={value}>{children}</QuizIdentityContext.Provider>
}
